import React, { useState, useEffect } from 'react'
import './cookieConsentModal.css'
import { DataPrivacyManager } from '@ampcore/data-privacy-manager'

export const CookieConsentModal = ({
  buttonText = 'Do Not Sell or Share My Personal Data',
  asButton = false,
}) => {
  const privacyManager = DataPrivacyManager.getInstance()
  const logoUrl = '/webfiles/latest/assets/cti/images/CTI-Logo.png'
  const [showModal, setShowModal] = useState(false)
  const handleClick = () => {
    setShowModal(true)
    window.addEventListener('keydown', handleKeyDown)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowModal(false)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }

  // Store the user's selection for the toggle
  const [consentOptIn, setConsentOptIn] = useState(true)
  const [consentToggleButton, setConsentToggleButton] = useState('toggle_on')
  const [showConfimation, setShowConfirmation] = useState(false)

  const handleToggle = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const toggle = e.target

    toggle.setAttribute('aria-checked', (!consentOptIn).toString())
    setConsentOptIn(!consentOptIn)
    setConsentToggleButton(consentOptIn ? 'toggle_off' : 'toggle_on')
  }

  const handleConfirmChoices = () => {
    setShowModal(false)
    if (consentToggleButton === 'toggle_on') {
      setConsentOptIn(true)
      privacyManager.optIn(true)
      setShowConfirmation(false)
    } else {
      setConsentOptIn(false)
      privacyManager.optOut(true)
      setShowConfirmation(true)
    }
  }

  useEffect(() => {
    // On component mount, check if the user has a saved preference using DataPrivacyManager
    const optedIn = privacyManager.getConsentState()
    setConsentToggleButton(optedIn ? 'toggle_on' : 'toggle_off')
    setConsentOptIn(optedIn)
    setShowConfirmation(!optedIn)
  }, [])

  const modalTitle = 'Do Not Sell or Share My Personal Data'
  const modalDisclosure = (
    <div id="ot-pc-desc" className="Consent-content">
      When you visit our website, we store cookies on your browser to collect
      information. We use cookies to share information with our advertising
      partners to assist in our marketing efforts. Specifically, we share your
      website activity with marketing providers to market products to you on
      other websites—cross-contextual behavioral advertising. We also use other
      cookies that are strictly necessary to ensure the proper functioning of
      our website and preserve your preferences. Those cookies are not used to
      market to you and cannot be turned off or limited. Further information is
      available in our{' '}
      <a
        className="Link"
        id="cookie-notice-link"
        href="/privacy-statement"
        data-analytics="onetrust-cookie-notice-link"
        tabindex="0"
      >
        Cookie Notice
      </a>
      . <br /> <br /> You have a right to request we not share your data for
      cross-contextual behavioral advertising. Use the below toggle slider to
      confirm your preferences. The gray toggle color means you have opted out
      of data sharing and we won't sell or share your data with third-party
      agencies. You can change your preferences and confirm your choices
      anytime.
    </div>
  )

  const alert = (
    <div class="ot-optout-signal">
      <div class="ot-optout-icon">
        <svg xmlns="http://www.w3.org/2000/svg">
          <path
            class="ot-floating-button__svg-fill"
            d="M14.588 0l.445.328c1.807 1.303 3.961 2.533 6.461 3.688 2.015.93 4.576 1.746 7.682 2.446 0 14.178-4.73 24.133-14.19 29.864l-.398.236C4.863 30.87 0 20.837 0 6.462c3.107-.7 5.668-1.516 7.682-2.446 2.709-1.251 5.01-2.59 6.906-4.016zm5.87 13.88a.75.75 0 00-.974.159l-5.475 6.625-3.005-2.997-.077-.067a.75.75 0 00-.983 1.13l4.172 4.16 6.525-7.895.06-.083a.75.75 0 00-.16-.973z"
            fill="#FFF"
            fill-rule="evenodd"
          ></path>
        </svg>
      </div>
      <span>Your Opt Out Preference Signal is Honored</span>
    </div>
  )

  const consentSection = (
    <section className="">
      <h3> Manage Consent Preferences</h3>
      <div className="Accordion">
        <details className="Accordion-item">
          <summary className="Accordion-toggle">
            Share or sale of personal data{' '}
            <div
              className="Accordion-toggleInput u-flex u-flexJustifyCenter u-flexAlignItemsCenter"
              role="switch"
              id="ot-group-id-SSPD_BG"
              aria-checked={consentOptIn}
              checked={consentOptIn}
              tabIndex="0"
              onClick={handleToggle}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleToggle(e)
                }
              }}
              data
            >
              <div
                className={`material-symbols-outlined u-textSizeMsrp4 ${consentOptIn ? 'toggleChecked' : ''}`}
              >
                {consentToggleButton}
              </div>
            </div>
          </summary>
          <div className="Accordion-content Content u-paddingLeft0">
            <div className="Accordion-subsection">
              <p>
                Under the CPRA, you have the right to opt out of the sale or
                sharing of your personal information to third-parties. These
                cookies collect information for analytics to personalize your
                experience with targeted ads. You may exercise your right to opt
                out of the sale or sharing of personal information by using this
                toggle switch. If you opt out, we will not be able to offer you
                personalized ads and will not hand over your personal
                information to any third-parties. If you have enabled privacy
                controls on your browser (such as a plugin), we have to take
                that as a valid request to opt out. Therefore we would not be
                able to track your activity through the web. This may affect our
                ability to personalize ads according to your preferences.
              </p>
            </div>
            <div className="Accordion-subsection">
              <h3>Targeting Cookies</h3>
              <p>
                These cookies may be set through our site by our advertising
                partners. They may be used by those companies to build a profile
                of your interests and show you relevant ads on other sites. They
                do not store directly personal information but are based on
                uniquely identifying your browser and internet device. If you do
                not allow these cookies, you will experience less targeted
                advertising.
              </p>
            </div>
            <div className="Accordion-subsection">
              <h3>Functional Cookies</h3>
              <p>
                These cookies enable the website to provide enhanced
                functionality and personalization. They may be set by us or by
                third-party providers whose services we have added to our pages.
                If you do not allow these cookies then some or all of these
                services may not function properly.
              </p>
            </div>
          </div>
        </details>
      </div>
    </section>
  )
  return (
    <div
      id="CookieConsentModal"
      className={`Footer-legalLink ${asButton ? 'u-sizeConstrained u-marginBottom4gu' : ' u-inline'}`}
    >
      {/* OneTrust Cookies Settings button start --> */}
      <span className="Footer-legalLink" id="Privacy and Security">
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- CTINEXT-4707 */}
        <a
          id="consent-btn"
          className={` ${asButton ? 'Button Button-color-action u-textNoTransform' : ' Link '} u-textSizeMsrm1 ot-sdk-show-settings`}
          data-analytics={`${asButton ? 'onetrust-cta-personal-data-btn' : 'onetrust-cta-btn'}`}
          onClick={handleClick}
        >
          {buttonText}
        </a>
      </span>
      {showModal && (
        <div className="Modal">
          <dialog className="Modal-window">
            <header className="Modal-header">
              <div
                className="ot-pc-logo"
                role="img"
                aria-label="Columbia Threadneedle Logo"
              >
                <img alt="Columbia Threadneedle Logo" src={logoUrl} />
              </div>
              <button
                className="Button Button--icon Modal-icon-button u-decoBackgroundTransparent"
                onClick={() => setShowModal(false)}
              >
                <span className="u-hiddenVisually">Close this dialog</span>
                <span
                  className="material-symbols-outlined"
                  role="img"
                  title="close"
                  aria-label="close"
                  data-analytics="onetrust-modal-close-btn"
                >
                  close
                </span>
              </button>
            </header>
            <div className="Modal-content Content">
              {showConfimation && alert}
              <h3>{modalTitle}</h3>
              {modalDisclosure}
              {consentSection}
            </div>
            <footer className="Modal-footer u-flex u-flexJustifyContentEnd">
              <button
                className="Button Button--primary u-textNoTransform Button-color-action u-flexExpandLeft"
                onClick={handleConfirmChoices}
                data-analytics="onetrust-confirm-choices-btn"
              >
                Confirm My Choices
              </button>
            </footer>
          </dialog>
        </div>
      )}
    </div>
  )
}
